/**
 * Idle-gated falling shamrocks — a config of the shared falling-particle effect.
 * Each shamrock is a three-leaflet trefoil with a little stem, tumbling down in
 * a mix of greens (each picks one of the `--shamrock-1..3` theme vars) so the
 * flock recolors light/dark.
 */
import { createFallingEffect } from './createFallingEffect';

const SHAMROCK_VARS = ['--shamrock-1', '--shamrock-2', '--shamrock-3'];
const DEFAULT_SHAMROCK = 'oklch(0.52 0.15 150)';
const FALLBACKS = [
  'oklch(0.52 0.15 150)',
  'oklch(0.43 0.14 152)',
  'oklch(0.63 0.16 145)',
];

// One heart-shaped leaflet, tip at the origin, lobes pointing up (~19u wide).
// Three of these rotated 120° apart form the trefoil. Built lazily (no Path2D
// in jsdom, which would break the settings-preview test).
const LEAFLET_D =
  'M0,0 C1,-3 4,-4 6,-4 C9.5,-4 9.5,-8.5 6,-9.5 C4,-10.3 1,-9.3 0,-6 C-1,-9.3 -4,-10.3 -6,-9.5 C-9.5,-8.5 -9.5,-4 -6,-4 C-4,-4 -1,-3 0,0 Z';
// Center spine + two side veins per leaflet.
const VEIN_D = 'M0,-1 L0.3,-7.5 M0,-3.5 L4,-6 M0,-3.5 L-4,-6';
// Soft sheen arc across the upper lobe.
const HIGHLIGHT_D = 'M0,-6.5 C2.5,-6 4.5,-6.5 5.6,-7.6';
// Gently curved tapered stem through the gap between the lower leaflets.
const STEM_D = 'M-0.8,3 Q-0.6,7 0.2,11 L1.2,11 Q0.6,7 0.8,3 Z';
const LEAF_ANGLES = [0, 120, 240];
const VEIN_STROKE = 'rgba(0,0,0,0.22)';
const HIGHLIGHT_STROKE = 'rgba(255,255,255,0.24)';
let leafletPath: Path2D | undefined;
let stemPath: Path2D | undefined;
let veinPath: Path2D | undefined;
let highlightPath: Path2D | undefined;

/** Run `draw` once per leaflet, rotated into the three trefoil positions. */
function eachLeaf(ctx: CanvasRenderingContext2D, draw: () => void): void {
  for (const deg of LEAF_ANGLES) {
    ctx.save();
    ctx.rotate((deg * Math.PI) / 180);
    draw();
    ctx.restore();
  }
}

/** Draw a detailed shamrock centered at the origin (engine sets fill + transform). */
function drawShamrock(ctx: CanvasRenderingContext2D, size: number): void {
  const leaf = (leafletPath ??= new Path2D(LEAFLET_D));
  const stem = (stemPath ??= new Path2D(STEM_D));
  const veins = (veinPath ??= new Path2D(VEIN_D));
  const sheen = (highlightPath ??= new Path2D(HIGHLIGHT_D));
  ctx.save();
  ctx.scale(size / 20, size / 20);
  ctx.lineCap = 'round';
  ctx.fill(stem);
  eachLeaf(ctx, () => ctx.fill(leaf));
  ctx.strokeStyle = VEIN_STROKE;
  ctx.lineWidth = 0.55;
  eachLeaf(ctx, () => ctx.stroke(veins));
  ctx.strokeStyle = HIGHLIGHT_STROKE;
  ctx.lineWidth = 0.8;
  eachLeaf(ctx, () => ctx.stroke(sheen));
  ctx.restore();
}

/** Full-screen idle falling shamrocks (multi-green, tumbling). */
export const ShamrocksOverlay = createFallingEffect({
  testId: 'shamrocks-overlay',
  config: {
    count: 48,
    size: [13, 22],
    speed: [0.5, 1.1],
    drift: [-0.4, 0.4],
    sway: [14, 30],
    swayFreq: [0.012, 0.03],
    spin: [-0.05, 0.05],
    alpha: [0.85, 1],
  },
  fill: canvas =>
    SHAMROCK_VARS.map(
      (v, i) =>
        getComputedStyle(canvas).getPropertyValue(v).trim() || FALLBACKS[i] || DEFAULT_SHAMROCK
    ),
  draw: drawShamrock,
});
